import { TableTextFilter } from "components/TableTextFilter/TableTextFilter";
import { setScrollBehavior } from "features/scrollPageBehaviorProvider/scrollPageBehaviorProvider";
import { _ } from "i18n";
import {DiscoverPlayersRowResponse} from "libs/apiClient";
import client from "libs/client";
import { useEffect, useState } from "react";
import DatagridV2, { type SimpleColumn } from "ui-kit/src/Datagrid/DatagridV2";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import { PositionChoices } from "features/discover/types";
import {createSortArrows} from "features/discover/functions";

const entityTypeOptions = [
	{
		label: 'Discover',
		value: 'Discover',
	},
	{
		label: 'Partner',
		value: 'Partner',
	},
	{
		label: 'NonPartner',
		value: 'NonPartner',
	},
];

const hasUserOptions= [{
	label: "No (dummy)",
	value: "Dummy",
}, {
	label: "Yes",
	value: "Real",
}]

export function DiscoverPlayersTable({
	columnVisibility,
	defaultFilters = {},
	rowAction = undefined,
}: {
	columnVisibility?: Record<string, boolean>;
	defaultFilters?: { [key: string]: string | number | undefined };
	rowAction?: (row: DiscoverPlayersRowResponse) => void;
}) {
    const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({
        pageIndex: 0,
        pageSize: 15,
    });

    const [filters, setFilters] = useState<{ [key: string]: string | number | undefined }>({ ...defaultFilters});
    const [filterValues, setFilterValues] = useState<{ [key: string]: string | number | undefined }>({ ...defaultFilters});
    const [sort, setSort] = useState<{ field: string, direction: string }>();

    useEffect(() => { setScrollBehavior() }, []);

    const { data, isLoading } = client.getDiscoverPlayers.useQuery({
        page: pagination.pageIndex + 1,
		limit: pagination.pageSize,
		filters: [
            ...(filters?.id ? [{
                field: "id",
                type: "search",
                value: filters.id,
            }] : []),
            ...(filters?.playerData ? [{
                field: "playerData",
                type: "exact",
                value: filters.playerData,
            }] : []),
            ...(filters?.name ? [{
                field: "name",
                type: "search",
                value: filters.name,
            }] : []),
            ...(filters?.club ? [{
                field: "club",
                type: "search",
                value: filters.club,
            }] : []),
            ...(filters?.team ? [{
                field: "team",
                type: "search",
                value: filters.team,
            }] : []),
            ...(filters?.teamId ? [{
                field: "teamId",
                type: "exact",
                value: filters.teamId,
            }] : []),
            ...(filters?.entityType ? [{
                field: "entityType",
                type: "exact",
                value: filters.entityType,
            }] : []),
            ...(filters?.position ? [{
                field: "position",
                type: "exact",
                value: filters.position,
            }] : []),
            ...(filters?.numberSign ? [{
                field: "numberSign",
                type: "search",
                value: filters.numberSign,
            }] : []),
            ...(filters?.isDummy ? [{
                field: "isDummy",
                type: "exact",
                value: filters.isDummy,
            }] : []),
        ],
        order: sort
    })

    const handleSetFilter = (key: string, value?: number | string,) => {
        setFilters({ ...filters, [key]: value });
        setPagination({ ...pagination, pageIndex: 0 });
    }

    const handleClearFilter = (key: string) => {
        setFilters({ ...filters, [key]: undefined })
    }

	const handleSort = (newSort?: {field: string, direction: string}) => {
		setSort(newSort);
	}

    const generateColumnHeader = (columnName: string, label: string, hasSort: boolean = true, className?: string) => () => (
        <div className={`tableHeader ${className}`}>
            <TableTextFilter
                value={(filterValues[columnName] || "").toString()}
                onChange={(value) => setFilterValues({ ...filterValues, [columnName]: value })}
                clear={() => {
                    handleClearFilter(columnName);
                    setFilterValues({ ...filterValues, [columnName]: undefined })
                }}
                onSubmit={(value: string) => handleSetFilter(columnName, value)}
                placeholder={label}
            />
            {hasSort ? createSortArrows(columnName, sort, handleSort) : ''}
        </div>
    );

    const simpleColumns: SimpleColumn<DiscoverPlayersRowResponse>[] = [
        {
            id: "id",
            key: "id",
            header: generateColumnHeader("id", "Player ID", true),
        },
        {
            id: "playerDataId",
            key: "playerDataId",
            header: generateColumnHeader("playerData", "Player data ID", true),
        },
		{
			id: "entityType",
			key: "entityType",
			header: () => <div className="tableHeader tableHeader--select">
				<SelectComponent
					value={entityTypeOptions.find((o) => o.value === filters?.entityType)}
					handleChange={(value) => handleSetFilter("entityType", value?.value)}
					placeholder="Type"
					items={entityTypeOptions}
					isCleareable={false}
				/>
			</div>,
			cell: (value, row) => <div className="thinCell">{row.entityType}</div>
		},
		{
			id: "name",
			key: "name",
			header: generateColumnHeader("name", "Name"),
		},
		{
			id: "club",
			key: "club",
			header: generateColumnHeader("club", "Club", false),
			cell: (value, row) => row.club ? `${row.club.name} (${row.club.id})` : '',
		},
		{
			id: "team",
			key: "team",
			header: generateColumnHeader("team", "Team", false),
			cell: (value, row) => row.team ? `${row.team.name} (${row.team.id})` : '',
		},
        {
            id: "position",
            key: "position",
			header: () => <SelectComponent
				value={PositionChoices.find((o) => o.value === filters?.position)}
				handleChange={(value) => handleSetFilter("position", value?.value)}
				placeholder="Position"
				items={PositionChoices}
				isCleareable
			/>,
			cell: (value, row) => <div className="thinCell">{row.position}</div>,
        },
		{
			id: "numberSign",
			key: "numberSign",
			header: generateColumnHeader("numberSign", "Number sign", true),
			cell: (value, row) => <div className="thinCell">{row.numberSign}</div>
		},
		{
			id: "isDummy",
			key: "isDummy",
			cell: (value) => `${value ? 'No' : 'Yes'}`,
			header: () => <div className="tableHeader tableHeader--select">
				<SelectComponent
					value={hasUserOptions.find((o) => o.value === filters?.isDummy)}
					handleChange={(value) => handleSetFilter("isDummy", value?.value)}
					placeholder="Has user"
					items={hasUserOptions}
					isCleareable
				/>
			</div>,
		},
	];

	return (
		<DatagridV2
			rowAction={rowAction}
			getRowLink={rowAction ? undefined : (row) => `/discover/player-data/${row.playerDataId}`}
			isLoading={isLoading}
			simpleColumns={simpleColumns}
			data={data?.data.items ?? []}
			pageCount={data?.data?.pages ?? 1}
			pagination={pagination}
			setPagination={setPagination}
			noResults={_("datatables.zero_records")}
			columnVisibility={columnVisibility}
		/>
	)
}
